import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import type {
  FamilyInformationField,
  FamilyInformationFieldKey,
  FamilyInformationMember,
  PatchPatientBody,
  PatientInformationField,
} from "./patientInformation.types";
import {
  createEmptyFamilyMember,
  createFamilyInformationDraft,
  createInformationDraft,
  normalizeFamilyInformation,
} from "./patientInformation.utils";

export function usePatientProfileInformationController(
  patient: PatientProfilePatient,
  actions: PatientProfileBridgeActions
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const canEdit = auth.isAllowedTo("edit_patient_page_information");
  const [draft, setDraft] = React.useState<ReturnType<typeof createInformationDraft> | null>(null);
  const [activeField, setActiveField] = React.useState<PatientInformationField | null>(null);
  const [familyDraft, setFamilyDraft] = React.useState<FamilyInformationMember[] | null>(null);
  const [activeFamilyField, setActiveFamilyField] =
    React.useState<FamilyInformationFieldKey | null>(null);
  const isEditing = draft !== null;
  const updatePatientMutation = useMutation({
    mutationFn: (body: Partial<PatchPatientBody>) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: { id: patient.id, ...body },
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        const cacheSafeBody = { ...body };
        delete cacheSafeBody.socialWorker;
        delete cacheSafeBody.source;

        return { ...currentPatient, ...cacheSafeBody };
      });
      queryClient.invalidateQueries({ queryKey: ["patient", "get", patient.id] });
      toast({
        description: "Patient information updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update patient information.",
        status: "error",
      });
    },
  });

  const activateField = (field: PatientInformationField) => {
    if (draft === null) {
      return;
    }

    setActiveField(field);
  };

  const cancelField = () => {
    setDraft(createInformationDraft(patient));
    setActiveField(null);
  };

  const commitField = () => {
    if (draft === null) {
      return;
    }

    updatePatientMutation.mutate(draft, {
      onSuccess: () => setActiveField(null),
    });
  };

  const toggleEditing = () => {
    if (draft === null) {
      setDraft(createInformationDraft(patient));
      setFamilyDraft(createFamilyInformationDraft(patient));
      return;
    }

    setDraft(null);
    setActiveField(null);
    setFamilyDraft(null);
    setActiveFamilyField(null);
  };

  const invalidatePatient = () => {
    queryClient.invalidateQueries({ queryKey: ["patient", "get", patient.id] });
    queryClient.refetchQueries({ queryKey: ["patient", "get", patient.id], type: "active" });
  };

  const openChangeStatus = () => {
    if (!isEditing) {
      return;
    }

    actions.openChangeStatus({
      patient,
      onDone: () => {
        queryClient.invalidateQueries({
          queryKey: ["patientProfileModal", patient.id, "statusChanges"],
        });
        invalidatePatient();
      },
    });
  };

  const updateFamilyMember = (
    index: number,
    field: FamilyInformationField,
    value: FamilyInformationMember[FamilyInformationField]
  ) => {
    setFamilyDraft((currentDraft) => {
      if (currentDraft === null) {
        return currentDraft;
      }

      return currentDraft.map((member, memberIndex) =>
        memberIndex === index ? { ...member, [field]: value } : member
      );
    });
  };

  const addFamilyMember = () => {
    setFamilyDraft((currentDraft) => [...(currentDraft ?? []), createEmptyFamilyMember()]);
  };

  const cancelFamilyField = () => {
    setFamilyDraft(createFamilyInformationDraft(patient));
    setActiveFamilyField(null);
  };

  const commitFamilyField = () => {
    if (familyDraft === null) {
      return;
    }

    const normalizedFamilyDraft = normalizeFamilyInformation(familyDraft);

    if (normalizedFamilyDraft === null) {
      toast({
        description: "Invalid family phone number.",
        status: "error",
      });
      return;
    }

    updatePatientMutation.mutate(
      { familyInformation: normalizedFamilyDraft },
      { onSuccess: () => setActiveFamilyField(null) }
    );
  };

  return {
    activateField,
    activeFamilyField,
    activeField,
    addFamilyMember,
    canEdit,
    cancelFamilyField,
    cancelField,
    commitFamilyField,
    commitField,
    draft,
    familyDraft,
    isEditing,
    isSaving: updatePatientMutation.isPending,
    openChangeStatus,
    setActiveFamilyField,
    setDraft,
    toggleEditing,
    updateFamilyMember,
  };
}
